'use client'

import { useState, useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { type PropsWithChildren } from 'react'
import Header from '@/blocks/redesign_blocks/Header'
import Footer from '@/blocks/redesign_blocks/Footer'
import ServicesMenu from '@/blocks/redesign_blocks/ServicesMenu'
import Modal from '@/blocks/redesign_blocks/Modal'
import SuccessModal from '@/blocks/redesign_blocks/Modal/SuccessModal'
import CookieConsent from '@/blocks/redesign_blocks/CookieConsent'
import { ModalProvider, useModal } from '@/blocks/redesign_blocks/Modal/ModalContext'

function ConditionalLayoutInner({ children }: PropsWithChildren) {
  const pathname = usePathname()
  const prevPathnameRef = useRef(pathname)
  const isTestIntegration = pathname.startsWith('/test-integration')
  const { isModalOpen, modalType, openProjectModal, openConsultationModal, openServiceModal, closeModal, isSuccessModalOpen, openSuccessModal, closeSuccessModal } = useModal()
  const useFinRecipientForModal =
    /^\/it-solutions\/b2[bc]\/?/.test(pathname) && modalType === 'service'
  const [isServicesMenuOpen, setIsServicesMenuOpen] = useState(false)
  const [showCookieConsent, setShowCookieConsent] = useState(false)

  // Закрываем ServicesMenu при изменении размера экрана
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth
      const isDesktop = width >= 1440
      
      // ServicesMenu должно быть открыто только на десктопе
      // Если перешли на планшет/мобилку - закрываем его
      if (!isDesktop && isServicesMenuOpen) {
        setIsServicesMenuOpen(false)
      }
    }

    // Проверяем при монтировании
    checkScreenSize()

    // Подписываемся на изменения размера окна
    window.addEventListener('resize', checkScreenSize)

    return () => {
      window.removeEventListener('resize', checkScreenSize)
    }
  }, [isServicesMenuOpen])

  // Проверяем localStorage и показываем cookie consent через 2 секунды после загрузки страницы
  useEffect(() => {
    // Проверяем, не принял ли пользователь уже cookies
    const cookieConsent = localStorage.getItem('cookieConsent')
    if (cookieConsent === 'accepted') {
      return // Не показываем cookie consent
    }
    
    // Показываем cookie consent через 2 секунды
    const timer = setTimeout(() => {
      setShowCookieConsent(true)
    }, 2000)
    
    return () => clearTimeout(timer)
  }, [])

  // Закрываем меню и модальные окна при смене страницы
  useEffect(() => {
    if (prevPathnameRef.current !== pathname) {
      setIsServicesMenuOpen(false)
      closeModal()
      closeSuccessModal()
      prevPathnameRef.current = pathname
    }
  }, [pathname, closeModal, closeSuccessModal])

  const toggleServicesMenu = () => {
    setIsServicesMenuOpen(!isServicesMenuOpen)
  }

  const closeServicesMenu = () => {
    setIsServicesMenuOpen(false)
  }

  const openFooterProjectModal = () => {
    openProjectModal()
  }

  const openFooterConsultationModal = () => {
    openConsultationModal()
  }

  const handleAcceptCookies = () => {
    setShowCookieConsent(false)
    localStorage.setItem('cookieConsent', 'accepted')
  }

  return (
    <>
      <Header 
        onServicesClick={toggleServicesMenu} 
        onProjectClick={openProjectModal}
        onConsultationClick={openConsultationModal}
      />
      
      <ServicesMenu 
        isOpen={isServicesMenuOpen} 
        onClose={closeServicesMenu}
        onConsultationClick={openConsultationModal}
        onProjectClick={openProjectModal}
      />
      
      <main className="appContent">
        {children}
      </main>
      
      <Footer 
        onProjectClick={openFooterProjectModal} 
        onConsultationClick={openFooterConsultationModal}
      />
      
      <Modal 
        isOpen={isModalOpen}
        onClose={closeModal}
        title={modalType === 'project' ? 'Обсудим ваш проект' : modalType === 'consultation' ? 'Получить консультацию' : 'Подключить сервис?'}
        subtitle="Заполните форму и мы свяжемся с вами в ближайшее время"
        useFinRecipient={useFinRecipientForModal}
        onSuccess={() => { 
          openSuccessModal()
          closeModal()
        }}
      />

      <SuccessModal 
        isOpen={isSuccessModalOpen} 
        onClose={closeSuccessModal}
        manageScroll={false}
      />
      
      <CookieConsent 
        isVisible={showCookieConsent}
        onAccept={handleAcceptCookies}
      />
    </>
  )
}

export default function ConditionalLayout({ children }: PropsWithChildren) {
  return (
    <ModalProvider>
      <ConditionalLayoutInner>
        {children}
      </ConditionalLayoutInner>
    </ModalProvider>
  )
}
